import { useState } from 'react';
import { useCancelPriceMigration, usePriceMigrations } from '../hooks/usePlanPricing';
import {
  PlanPriceMigrationAccountStatus,
  PlanPriceMigrationStatus,
  type PlanPriceMigration,
} from '../types/plans.types';
import { formatPlanPrice } from '../utils/plan-sync-status';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { StatusBadge, type StatusVariant } from '@/shared/components/ui/status-badge';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { formatIsoDate } from '@/shared/utils/format-date.utils';

interface PriceMigrationPanelProps {
  planId: string;
}

const MIGRATION_STATUS: Record<string, { variant: StatusVariant; label: string }> = {
  [PlanPriceMigrationStatus.Scheduled]: { variant: 'warning', label: 'Agendada' },
  [PlanPriceMigrationStatus.Completed]: { variant: 'active', label: 'Completada' },
  [PlanPriceMigrationStatus.Cancelled]: { variant: 'inactive', label: 'Detenida' },
};

function MigrationCard({
  migration,
  onRequestCancel,
}: {
  migration: PlanPriceMigration;
  onRequestCancel: (migrationId: string) => void;
}) {
  const status = MIGRATION_STATUS[migration.status] ?? {
    variant: 'inactive',
    label: migration.status,
  };
  const isScheduled = migration.status === PlanPriceMigrationStatus.Scheduled;

  return (
    <div
      data-testid={`price-migration-${migration.id}`}
      className="flex flex-col gap-3 rounded-xl border border-outline p-4"
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <StatusBadge status={status.variant} label={status.label} />
          <span className="text-light text-sm">
            {formatPlanPrice(migration.oldPriceMonthly)} →{' '}
            {formatPlanPrice(migration.newPriceMonthly)} mensual
          </span>
        </div>
        {isScheduled && (
          <UnifiedButton
            variant="outline"
            data-testid={`cancel-migration-btn-${migration.id}`}
            onClick={() => onRequestCancel(migration.id)}
          >
            Detener
          </UnifiedButton>
        )}
      </div>

      <p data-testid={`migration-effective-${migration.id}`} className="text-primary text-xs">
        Aplica el {formatIsoDate(migration.effectiveAt)}
      </p>

      <div className="flex flex-wrap gap-4 text-xs">
        <span data-testid={`migration-total-${migration.id}`} className="text-light">
          {migration.totalAccounts} cuentas
        </span>
        <span className="text-primary">Pendientes: {migration.pendingCount}</span>
        <span className="text-positive">Aplicadas: {migration.appliedCount}</span>
        <span className="text-primary">Anuladas: {migration.cancelledCount}</span>
        <span className="text-negative">Fallidas: {migration.failedCount}</span>
      </div>

      {migration.failedCount > 0 && (
        <div className="flex flex-col gap-1">
          {migration.accounts
            .filter((a) => a.status === PlanPriceMigrationAccountStatus.Failed)
            .map((account) => (
              <p
                key={account.tenantId}
                data-testid={`migration-failure-${account.tenantId}`}
                className="text-negative text-xs"
              >
                Cuenta {account.tenantId}: {account.failureReason ?? 'sin detalle'} (intento{' '}
                {account.attemptCount})
              </p>
            ))}
        </div>
      )}

      {migration.cancellationReason && (
        <p className="text-primary text-xs">Motivo: {migration.cancellationReason}</p>
      )}
    </div>
  );
}

export function PriceMigrationPanel({ planId }: PriceMigrationPanelProps) {
  const { migrations, isLoading } = usePriceMigrations(planId);
  const { cancelMigration, isCancelling } = useCancelPriceMigration(planId);
  const [cancellingId, setCancellingId] = useState<string>('');

  if (isLoading) {
    return <div data-testid="price-migrations-loading" className="py-8" aria-hidden />;
  }

  if (migrations.length === 0) {
    return (
      <p data-testid="price-migrations-empty" className="text-primary text-xs">
        Este plan no tiene migraciones de precio.
      </p>
    );
  }

  const handleConfirmCancel = () => {
    cancelMigration(
      { migrationId: cancellingId },
      {
        onSuccess: () => {
          Notification.success({ title: 'Migración detenida' });
          setCancellingId('');
        },
        onError: () => setCancellingId(''),
      },
    );
  };

  return (
    <div data-testid="price-migrations-panel" className="flex flex-col gap-3">
      {migrations.map((migration) => (
        <MigrationCard key={migration.id} migration={migration} onRequestCancel={setCancellingId} />
      ))}

      <ConfirmDialog
        open={!!cancellingId}
        onOpenChange={(open) => !open && setCancellingId('')}
        title="Detener la migración de precio"
        description="Solo alcanza a las cuentas que todavía no fueron migradas. A las que ya se les aplicó el precio nuevo se les queda; deshacer un cobro es otra operación. A quienes recibieron el aviso se les notifica la corrección."
        confirmLabel="Detener"
        cancelLabel="Cancelar"
        confirmActionType="destructive"
        onConfirm={handleConfirmCancel}
        isPending={isCancelling}
        data-testid="cancel-migration-dialog"
      />
    </div>
  );
}
